import { createLogger } from './logger.js'

const logger = createLogger('Clipboard')

/**
 * 统一剪贴板复制方法，兼容华为等移动端浏览器 HTTPS 环境。
 *
 * 策略：
 * 1. 优先 ClipboardItem（支持 HTML + 纯文本同时写入）
 * 2. writeText（纯文本，ClipboardItem 不存在或失败时）
 * 3. execCommand('copy')（writeText 失败或不存在时）
 * 4. 所有方案失败 → 返回 false
 *
 * @param {string} text - 要复制的纯文本内容
 * @param {string} [html] - 可选的 HTML 内容（ClipboardItem 模式使用）
 * @returns {Promise<boolean>} 是否复制成功
 */
export async function copyToClipboard(text, html) {
  // 1. ClipboardItem（支持 HTML 格式写入，桌面 Chrome 等）
  if (html && typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      })
      await navigator.clipboard.write([clipboardItem])
      logger.info('ClipboardItem 方式复制成功')
      return true
    } catch (err) {
      logger.warn('ClipboardItem 方式失败，降级:', err)
    }
  }

  // 2. writeText（纯文本，HTTPS 安全上下文可用）
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      logger.info('writeText 方式复制成功')
      return true
    } catch (err) {
      logger.warn('writeText 方式失败，降级:', err)
    }
  }

  // 3. execCommand('copy')（最终降级，华为等移动端主要靠这条）
  try {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-9999px'
    textArea.style.top = '0'
    textArea.style.opacity = '0'
    textArea.setAttribute('readonly', '')
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.setSelectionRange(0, textArea.value.length)
    const copied = document.execCommand('copy')
    document.body.removeChild(textArea)
    if (copied) {
      logger.info('execCommand 方式复制成功')
      return true
    }
    logger.warn('execCommand 返回 false，复制可能未成功')
  } catch (err) {
    logger.warn('execCommand 方式失败:', err)
  }

  logger.error('所有复制方式均失败')
  return false
}
